import { useCallback, useSyncExternalStore } from 'react'
import {
  FLIGHT_RANGE,
  getSavedRange,
  type DateRange,
  type Flight,
} from '@/services/stats/statsRange'

/**
 * One period for every stats view in the app: picking a period on any card
 * moves all of them, here and on every page opened after. Kept in localStorage
 * so a reload — or another tab — keeps it.
 */
const STORAGE_KEY = 'adnuntius.stats.dateRange'

const listeners = new Set<() => void>()

function readStored(): DateRange | null {
  try {
    return localStorage.getItem(STORAGE_KEY) ? getSavedRange(STORAGE_KEY, '24h') : null
  } catch {
    return null
  }
}

let stored = readStored()

function subscribe(listener: () => void) {
  listeners.add(listener)
  // Another tab picking a period moves this one's cards too.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return
    stored = readStored()
    listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

function setStored(next: DateRange) {
  stored = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Unsaved, the period still holds for this session.
  }
  listeners.forEach((listener) => listener())
}

/**
 * The period stats views are showing, shared app-wide.
 *
 * `defaultRange` is what this view shows until a period has been picked
 * anywhere. Pass the view's `flight` when it has one: "the campaign period"
 * means nothing to a view without dates, so such a view shows its default
 * instead — and the choice still holds when you go back to a campaign.
 */
export function useStatsRange(defaultRange: DateRange = '24h', flight?: Flight) {
  const shared = useSyncExternalStore(subscribe, () => stored)
  const hasFlight = !!(flight?.startDate && flight?.endDate)
  const range = !shared || (shared === FLIGHT_RANGE && !hasFlight) ? defaultRange : shared

  const change = useCallback((next: DateRange) => setStored(next), [])

  return [range, change] as const
}
